import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Currency, PaymentProvider, PixApiProvider, TranslationScopes } from "@finbackoffice/enums";
import {
    IDepositRemainderResponse,
    IExchangeRatePair,
    ISiteConfigs,
} from "@finbackoffice/clientbff-client";
import { useTranslation } from "react-i18next";
import { ClientBFFContext } from "../../providers";
import { getCurrencyConfig, round } from "../../helpers";
import { PixWalletDataContext } from "./pix-wallet-data.provider";

export const useFirstStepDeposit = ({
    siteConfigs,
    rates,
    depositRemainderResponse,
}: {
    siteConfigs: ISiteConfigs;
    rates: Record<Currency, IExchangeRatePair[]> | null;
    depositRemainderResponse: IDepositRemainderResponse | undefined;
}) => {
    const { t } = useTranslation(TranslationScopes.PixWallet);
    const client = useContext(ClientBFFContext);
    const { setDepositStep, setDepositData } = useContext(PixWalletDataContext);
    const [value, setValue] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [errorApi, setErrorApi] = useState<string[]>([]);
    const [exceededLimit, setExceededLimit] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const brlRate = rates?.[Currency.BRL]?.find((r) => r.toCurrency === "USD")?.rate;
    const currencyConfig = getCurrencyConfig(siteConfigs, Currency.BRL);
    const { min, max_usd } = currencyConfig.deposit || {};
    const max = brlRate && max_usd ? max_usd / Number(brlRate) : 0;
    const roundedMax = round(max, currencyConfig.digits);
    const userPeriodLimits = depositRemainderResponse?.max_period_limits?.[0];
    const userDepositLimit = userPeriodLimits
        ? round(
              Number(userPeriodLimits.amount) - Number(userPeriodLimits.actual_sum) || 0,
              currencyConfig.digits,
          )
        : undefined;

    const maxLimit = useMemo(() => {
        if (typeof userDepositLimit !== "number") return roundedMax;
        return roundedMax > userDepositLimit ? userDepositLimit : roundedMax;
    }, [roundedMax, userDepositLimit]);

    const isResponsibleLimit = useMemo(() => {
        if (typeof userDepositLimit !== "number") return false;
        return userDepositLimit < roundedMax;
    }, [roundedMax, userDepositLimit]);

    const depositProviders = useMemo(() => {
        const providersData = (siteConfigs as ISiteConfigs).payments.PixAPI?.deposit || {};
        return (Object.keys(providersData) as PixApiProvider[])
            .map((provider) => ({
                provider,
                ...providersData[provider]!,
            }))
            .filter((p) => p.enabled)
            .sort((a, b) => a.order - b.order);
    }, [siteConfigs]);

    const [selectedProvider, setSelectedProvider] = useState<PixApiProvider>(
        depositProviders[0]?.provider,
    );

    useEffect(() => {
        setErrorApi([]);
    }, [value, selectedProvider]);

    useEffect(() => {
        setSelectedProvider(depositProviders[0]?.provider);
    }, [depositProviders]);

    useEffect(() => {
        const numberValue = Number(value || 0);
        if (!value) {
            setError("");
            return;
        }
        if (numberValue < (min ?? 0) || numberValue > maxLimit) {
            setError(t("min_max_validation", { min, max: maxLimit }));
        } else {
            setError("");
        }
    }, [value, min, maxLimit, t]);

    const clickDeposit = useCallback(async () => {
        setErrorApi([]);
        setExceededLimit("");
        try {
            setLoading(true);
            const response = await client.initiateDeposit({
                provider: PaymentProvider.PixAPI,
                currency: Currency.BRL,
                amount: value,
                provider_data: {
                    pix_api_provider: selectedProvider,
                },
            });
            let base64QrCode = response.deposit?.meta.qr_code;

            if (selectedProvider === PixApiProvider.DigitoPay) {
                base64QrCode = `data:image/png;base64,${response.deposit?.meta.qr_code}`;
            }

            if (selectedProvider === PixApiProvider.Pagfast) {
                const sourceQrCode = await fetch(response.deposit!.meta.qr_code_location!);
                const qrCodeText = await sourceQrCode.text();
                base64QrCode = `data:image/png;base64,${qrCodeText}`;
            }
            setDepositData({ ...response, base64QrCode });
            setDepositStep("second");
        } catch (e: any) {
            const errors = e.response.data?.details ?? {};
            if (errors.max_24h_count_deposit_error) {
                setExceededLimit(errors.max_24h_count_deposit_error);
                return;
            }
            if (e.response.data?.error) {
                let detailedMessage = [e.response.data?.message];
                const details = e.response.data?.details;
                if (details) {
                    if (details.max_deposit_error) {
                        detailedMessage = [details.max_deposit_error];
                    } else {
                        for (const key in details) {
                            detailedMessage.push(details[key]);
                        }
                    }
                }

                if (detailedMessage.length > 0) {
                    setErrorApi(detailedMessage);
                    return;
                }
            }
            if (errors.error_message) {
                setErrorApi([errors.error_message]);
                return;
            }
            setErrorApi([e.response.data?.message || e.message]);
        } finally {
            setLoading(false);
        }
    }, [value, client, setDepositData, setDepositStep, selectedProvider]);

    return useMemo(
        () => ({
            clickDeposit,
            value,
            error,
            loading,
            min,
            maxLimit,
            currencyConfig,
            setValue,
            errorApi,
            exceededLimit,
            depositProviders,
            selectedProvider,
            isResponsibleLimit,
            setSelectedProvider,
        }),
        [
            clickDeposit,
            value,
            error,
            loading,
            setValue,
            min,
            maxLimit,
            currencyConfig,
            errorApi,
            exceededLimit,
            depositProviders,
            selectedProvider,
            isResponsibleLimit,
            setSelectedProvider,
        ],
    );
};
